import { CollisionSystem, HistoryApi, InvalidFramePlacementSystem, ModelLoadedEventSystem, PlannerCoreApi, SelectionEventSystem, MeasurementsWidgetSystem, HighlightAddedEntitySystem, UnavailableProductsEventSystem, ConflictOutlineSystem, ConflictSystem, HistoryEventSystem, OutlineSelectedFrameSystem, ProductInfoComponent, ProductType, ErrorMessages } from '@inter-ikea-storageone/range';
import { AutomaticGroupBuilderSystem, FunctionalMarginSystem, FunctionalMarginValidation, OutlineSystem, RequiredConnectorsSatisfiedValidation, StandardSelectionSystem, HierarchyUtils, ComponentIds } from '@inter-ikea-core/core';
import { BillyProductApi } from './BillyProductApi';
import { BillyEntityApi } from './BillyEntityApi';
import { BillySceneApi } from './BillySceneApi';
import { BillyCameraApi } from './BillyCameraApi';
import { SimpleOutlineSelectedSystem } from '@inter-ikea-core/core-toolbox';
import { DoorOnCornerFrameValidation } from './validations/DoorOnCornerFrameValidation';
import { FrameExtensionValidation } from './validations/FrameExtensionValidation';
import { CornerFittingsSystem } from './systems/CornerFittingsSystem';
import { CornerFrameMeasurementsSystem } from './systems/CornerFrameMeasurementsSystem';
import { MoveCollidingInteriorSystem } from './systems/MoveCollidingInteriorSystem';
import { IncludedShelfSystem } from './systems/IncludedShelfSystem';
export class BillyCoreApi extends PlannerCoreApi {
    bootstrap(config) {
        if (!this.core) {
            throw new Error('Core not initialized yet');
        }
        // Setup core base functionality
        const { selectionService, placementService } = super.initializeBaseCoreFunctionality();
        // Register core systems
        this.registerSystem(new OutlineSystem());
        this.registerSystem(new AutomaticGroupBuilderSystem(this.core.services.entityManager));
        this.registerSystem(new SimpleOutlineSelectedSystem());
        this.registerSystem(new StandardSelectionSystem(this.core.services.entityManager, this.core.services.sceneManager));
        this.registerSystem(new FunctionalMarginSystem());
        // Register core validations
        this.registerValidation(new RequiredConnectorsSatisfiedValidation());
        this.registerValidation(new FunctionalMarginValidation(false, this.core.services.entityManager));
        const collisionValidation = this.core
            .getValidations()
            .find((validation) => validation.id === 'Collision');
        collisionValidation.enableExhaustiveValidation();
        // Setup plannner APIs
        this.history = new HistoryApi(this);
        this.products = new BillyProductApi(this.core.services, placementService, this.reportErrorToSentry);
        this.entities = new BillyEntityApi(selectionService, this.core, placementService, this.ipexViewer, this.reportErrorToSentry, {
            onInvalidDuplicationPlacement: ({ selectedEntity, copyEntity }) => {
                // Handle placement result and display error messages
                const determinePlacementAttemptErrorMessage = () => {
                    const parentIsFrameExtension = HierarchyUtils.parent(selectedEntity)?.getComponent(ProductInfoComponent.id)?.productType === ProductType.BILLY_FRAME_EXTENSION;
                    const childIsFrameExtension = HierarchyUtils.getDescendantsWithComponent(selectedEntity, ComponentIds.FRAME_COMPONENT_ID)?.[0]?.getComponent(ProductInfoComponent.id)?.productType ===
                        ProductType.BILLY_FRAME_EXTENSION;
                    const extensionMaxedOut = parentIsFrameExtension || childIsFrameExtension;
                    if (extensionMaxedOut) {
                        return ErrorMessages.BillyExtensionMaxTwo;
                    }
                    return ErrorMessages.BillyExtensionWontFit;
                };
                const placementAttemptErrorMessage = determinePlacementAttemptErrorMessage();
                this.emit.onPlacementAttempt(placementAttemptErrorMessage);
                // If the placement should fail, this will remove the copyEntity from the entityManager
                this.core.services.entityManager.disposeEntity(copyEntity);
            }
        });
        // Register planner validations
        this.registerValidation(new DoorOnCornerFrameValidation(this));
        this.registerValidation(new FrameExtensionValidation(this));
        // Register planner systems
        this.registerSystem(new ConflictSystem(this));
        this.registerSystem(new ConflictOutlineSystem(this));
        this.registerSystem(new MoveCollidingInteriorSystem(this));
        this.registerSystem(new HistoryEventSystem(this));
        this.registerSystem(new UnavailableProductsEventSystem(this));
        this.registerSystem(new ModelLoadedEventSystem(this));
        this.registerSystem(new InvalidFramePlacementSystem(this));
        this.registerSystem(new CollisionSystem(this, []));
        this.registerSystem(new SelectionEventSystem(this));
        this.registerSystem(new HighlightAddedEntitySystem(this));
        this.registerSystem(new CornerFittingsSystem(this));
        this.registerSystem(new CornerFrameMeasurementsSystem(this));
        this.registerSystem(new OutlineSelectedFrameSystem(this));
        this.registerSystem(new IncludedShelfSystem(this));
        return this.loadProductCatalogs(config.productCatalogs);
    }
    getMainProductCatalog() {
        return this.catalogs['billy'];
    }
    initSceneApi() {
        this.scene = new BillySceneApi(this.core, this.ipexViewer, this.reportErrorToSentry);
        this.registerSystem(new MeasurementsWidgetSystem(this));
    }
    initCameraApi(canvas) {
        this.camera = new BillyCameraApi(this.core.services.sceneManager, this.ipexViewer, this.core.services.entityManager, this, canvas);
    }
}
//# sourceMappingURL=BillyCoreApi.js.map